import {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useState
} from "react";

import { useIntl } from "react-intl";

import FeatureGates from "@atlassian/feature-gate-js-client";

import { LoadingState } from "../../../../components/signup/bxpkit-signup/src/layouts/simple-signup-screen/simple-signup-form-view/component/loading-state";

import {
    useFetchUserSegmentData,
    useSearchParameters,
    useUserSegmentationStatus
} from "./";

export type JiraTemplateChoice = "kanban" | "scrum" | "business";

const CONTROL_COHORT = "control";
const VARIATION_COHORT = "variation";
const EXPERIMENT_KEY = "bxp_hide_the_robot_screen";

type ExperimentCohort = typeof CONTROL_COHORT | typeof VARIATION_COHORT;

type JiraTemplateChoiceProps = {
    shouldShowJiraTemplateChoiceScreen: boolean;
    templateChoice?: JiraTemplateChoice;
    setTemplateChoice: (templateChoice?: JiraTemplateChoice) => void;
    templateChoiceError: Error | undefined;
    setTemplateChoiceError: (e: Error) => void;
};

export const JiraTemplateChoiceContext = createContext<JiraTemplateChoiceProps>(
    {
        shouldShowJiraTemplateChoiceScreen: false,
        templateChoice: undefined,
        setTemplateChoice: () => undefined,
        templateChoiceError: undefined,
        setTemplateChoiceError: () => undefined
    }
);

export const JiraTemplateChoiceWithFatureFlagProvider = ({
    children
}: PropsWithChildren<unknown>) => {
    const jiraTemplateChoiceExperimentEnabled = FeatureGates.checkGate(
        "bxp_hide_the_robot_screen_gate"
    );

    if (!jiraTemplateChoiceExperimentEnabled) {
        // Make this component skip it's behaviour if the experiment is not enabled.
        // This may seem like it's breaking the rules of hooks, as we're conditionally not calling the hooks below.
        // However, this is safe to do as long as `jiraTemplateChoiceExperimentEnabled` does not change between renders.
        // We don't expect this value to change between renders. It would only change between page refreshes. Therefore,
        // it's fine to exit early here.
        return <>{children}</>;
    }

    return <JiraTemplateChoiceProvider>{children}</JiraTemplateChoiceProvider>;
};

const JiraTemplateChoiceProvider = ({
    children
}: PropsWithChildren<unknown>) => {
    const { products } = useSearchParameters();
    const { canShowJiraTemplateChoiceScreen, loading } =
        useCanShowJiraTemplateChoiceScreen(products);

    const [templateChoice, setTemplateChoice] = useState<
        JiraTemplateChoice | undefined
    >();

    const { userSegmentationPageIsFinished } =
        useUserSegmentationStatus(products);

    const [templateChoiceError, setTemplateChoiceError] = useState<
        Error | undefined
    >();

    const userHasPickedTemplate = templateChoice !== undefined;

    const shouldShowJiraTemplateChoiceScreen =
        templateChoiceError === undefined &&
        canShowJiraTemplateChoiceScreen &&
        isInVariationCohort() &&
        !userHasPickedTemplate;

    useEffect(() => {
        /* We fire exposure event if the user passes all requisites to see the screen,
     and they have finished the user segmentation survey already. */
        if (
            !loading &&
            canShowJiraTemplateChoiceScreen &&
            userSegmentationPageIsFinished
        ) {
            fireExposureEvent();
        }
    }, [
        userSegmentationPageIsFinished,
        canShowJiraTemplateChoiceScreen,
        loading,
        fireExposureEvent
    ]);

    return loading ? (
        <LoadingState />
    ) : (
        <JiraTemplateChoiceContext.Provider
            value={{
                templateChoice,
                setTemplateChoice,
                shouldShowJiraTemplateChoiceScreen,
                templateChoiceError,
                setTemplateChoiceError
            }}
        >
            {children}
        </JiraTemplateChoiceContext.Provider>
    );
};

/* The user must pass the following conditions to see the template choice screen
   - locale: The user locale should be 'en' since this experiment will only run for the EN locale due to a
     short experiment lead time, factoring out translations to other locales
   - !hasTemplateRecommendation: Supplied via a query param, there must be no `templateId`
   - isProvisioningOnlyJSW: Provisioning only JSW and no other products
   - isProvisioningUserSegProducts: Is provisioning user segmentation products (which should be true if only JSW)
   - !isUserSegHiddenByQueryParam: User does not have `hideUserSegmentation=true` as a query param
   - !alreadyViewedUserSegmentationScreen: User is seeing segmentation screen for the first time. The
     user segmentation screen makes an API call to determine if the user has already seen the screen
     in the last 365 days. If they have seen it, the user seg screen is not displayed, and we do not meet
     our requirements to show the TTC either.
   - !isBusinessSegmentUser: The user must not be counted as a business user by user segmentation
   - !fetchError: This hook must access /gateway/api/me to check user segmentation and this call must succeed
     in order for the check above to be valid
 */
export const useCanShowJiraTemplateChoiceScreen = (
    products: readonly string[]
) => {
    const {
        isProvisioningUserSegProducts,
        isUserSegHiddenByQueryParam,
        alreadyViewedUserSegmentationScreen
    } = useUserSegmentationStatus(products);

    const { locale } = useIntl();

    const hasTemplateRecommendation = Boolean(useSearchParameters().templateId);
    const { fetched, fetchError, isBusinessSegmentUser } =
        useFetchUserSegmentData();

    const isProvisioningOnlyJSW =
        products.length === 1 && products[0] === "jira-software.ondemand";
    const exposedToUserSegScreen =
        isProvisioningUserSegProducts && !isUserSegHiddenByQueryParam;

    return {
        canShowJiraTemplateChoiceScreen:
            locale === "en" &&
            !hasTemplateRecommendation &&
            isProvisioningOnlyJSW &&
            exposedToUserSegScreen &&
            !alreadyViewedUserSegmentationScreen &&
            !isBusinessSegmentUser &&
            !fetchError,
        loading: !fetched
    };
};

export const useJiraTemplateChoice = () => {
    return useContext(JiraTemplateChoiceContext);
};

// Checks whether the user is in the variation cohort without firing the exposure event
export function isInVariationCohort() {
    return (
        FeatureGates.getExperimentValue<ExperimentCohort>(
            EXPERIMENT_KEY,
            "cohort",
            CONTROL_COHORT,
            { fireExperimentExposure: false }
        ) === VARIATION_COHORT
    );
}

function fireExposureEvent() {
    FeatureGates.manuallyLogExperimentExposure(EXPERIMENT_KEY);
}
